import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  Button,
} from '@mui/material';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import FlightIcon from '@mui/icons-material/Flight';
import MapIcon from '@mui/icons-material/Map';
import TerrainIcon from '@mui/icons-material/Terrain';
import CodeIcon from '@mui/icons-material/Code';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SatelliteAltIcon from '@mui/icons-material/SatelliteAlt';
import ExploreIcon from '@mui/icons-material/Explore';
import { alpha } from '@mui/material/styles';

const solutions = [
  // Aerial Services Group
  {
    id: 'aerial-photography',
    name: 'Aerial & LiDAR Mapping',
    icon: <FlightIcon sx={{ fontSize: 40 }} />,
    description: 'High-resolution aerial imagery and LiDAR scanning for precise terrain and 3D modeling',
    path: '/solutions/aerial-photography',
    color: '#2196f3'
  },
  {
    id: 'drone-mapping',
    name: 'Drone Mapping',
    icon: <SatelliteAltIcon sx={{ fontSize: 40 }} />,
    description: 'UAV-based mapping solutions for detailed aerial surveys and site inspections',
    path: '/solutions/drone-mapping',
    color: '#1976d2'
  },
  {
    id: 'satellite-imagery',
    name: 'Satellite Imaging',
    icon: <ExploreIcon sx={{ fontSize: 40 }} />,
    description: 'Advanced satellite imaging and remote sensing for large-scale mapping projects',
    path: '/solutions/satellite-imagery',
    color: '#0d47a1'
  },
  // Survey Services Group
  {
    id: 'geodetic-survey',
    name: 'Geodetic Survey',
    icon: <TerrainIcon sx={{ fontSize: 40 }} />,
    description: 'High-precision geodetic control networks and reference frame establishment',
    path: '/solutions/geodetic-survey',
    color: '#4caf50'
  },
  {
    id: 'hydrographic-survey',
    name: 'Hydrographic Survey',
    icon: <LocationOnIcon sx={{ fontSize: 40 }} />,
    description: 'Underwater terrain mapping and marine surveying services',
    path: '/solutions/hydrographic-survey',
    color: '#2196f3'
  },
  {
    id: 'geophysical-survey',
    name: 'Geophysical Survey',
    icon: <TerrainIcon sx={{ fontSize: 40 }} />,
    description: 'Subsurface investigation and geological mapping services',
    path: '/solutions/geophysical-survey',
    color: '#ff9800'
  },
  {
    id: 'cadastral-survey',
    name: 'Cadastral Survey',
    icon: <MapIcon sx={{ fontSize: 40 }} />,
    description: 'Legal land boundary surveys and property mapping services',
    path: '/solutions/cadastral-survey',
    color: '#f44336'
  },
  // GIS & Software Group
  {
    id: 'gis-mapping',
    name: 'GIS Mapping',
    icon: <MapIcon sx={{ fontSize: 40 }} />,
    description: 'Comprehensive GIS development and spatial analysis solutions',
    path: '/solutions/gis-developments',
    color: '#9c27b0'
  },
  {
    id: 'cartography',
    name: 'Cartography',
    icon: <LocationOnIcon sx={{ fontSize: 40 }} />,
    description: 'Professional map design and production services',
    path: '/solutions/cartography',
    color: '#e91e63'
  },
  {
    id: 'lims',
    name: 'Land Information Management',
    icon: <CodeIcon sx={{ fontSize: 40 }} />,
    description: 'Integrated land information management systems for efficient property administration',
    path: '/solutions/lims',
    color: '#795548'
  },
  {
    id: 'software-development',
    name: 'Software Development',
    icon: <CodeIcon sx={{ fontSize: 40 }} />,
    description: 'Custom GIS applications and enterprise solutions development',
    path: '/solutions/software-development',
    color: '#607d8b'
  }
];

const Overview = () => {
  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: '40vh',
          display: 'flex',
          alignItems: 'center',
          paddingTop: { xs: '120px', md: '140px' },
          paddingBottom: '40px',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("/images/solutions.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: 0,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(0,64,64,0.9), rgba(0,0,0,0.7))',
            zIndex: 1,
          },
        }}
      >
        <Container sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h1"
              color="white"
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                mb: 2,
              }}
            >
              Our Solutions
            </Typography>
            <Typography
              variant="h5"
              color="white"
              sx={{
                maxWidth: '800px',
                opacity: 0.9,
                fontSize: { xs: '1rem', sm: '1.2rem', md: '1.4rem' },
                lineHeight: 1.6,
              }}
            >
              Comprehensive geospatial services tailored to transform your business needs into reality
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Solutions Grid */}
      <Box sx={{ py: 8, backgroundColor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Grid 
            container 
            spacing={3}
            justifyContent="center"
          >
            {solutions.map((solution, index) => (
              <Grid 
                item 
                xs={12} 
                sm={6}
                md={4}
                key={index}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card 
                    sx={{ 
                      p: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease',
                      borderRadius: 2,
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: (theme) => `0 8px 24px ${alpha(solution.color, 0.2)}`,
                        '& .solution-icon': {
                          transform: 'scale(1.1)',
                        },
                        '& .learn-more-btn': {
                          backgroundColor: solution.color,
                        }
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '4px',
                        height: '100%',
                        backgroundColor: solution.color,
                      }
                    }}
                  >
                    <Box 
                      className="solution-icon"
                      sx={{ 
                        color: solution.color,
                        mb: 2,
                        transition: 'transform 0.3s ease',
                      }}
                    >
                      {solution.icon}
                    </Box>
                    <Typography 
                      variant="h5" 
                      gutterBottom
                      sx={{ 
                        fontSize: { xs: '1.25rem', md: '1.4rem' },
                        fontWeight: 600,
                        color: '#2c3e50',
                      }}
                    >
                      {solution.name}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 2,
                        flex: 1,
                        fontSize: '0.9rem',
                        lineHeight: 1.6,
                      }}
                    >
                      {solution.description}
                    </Typography>
                    <Button
                      component={Link}
                      to={solution.path}
                      variant="contained"
                      className="learn-more-btn"
                      size="small"
                      sx={{
                        mt: 'auto',
                        backgroundColor: 'primary.main',
                        color: 'white',
                        textTransform: 'none',
                        borderRadius: 1.5,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: solution.color,
                          transform: 'translateX(5px)',
                        }
                      }}
                    >
                      Learn More →
                    </Button>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Overview; 